import './App.css';
import {useState, useEffect, useRef, useCallback} from "react";
import { Routes, Route, useLocation } from 'react-router-dom';
// Removed MUI date picker dependencies - using native inputs instead
import {AppContext, UserData} from "./context/context";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {Context
        , GlobalContext
        , MapSettingsContext
        , UserDetails
        , LocationSettingsContext
        , Layer
        , ModeSettings
        , Fleet
        , Service
        , MapActionsContext
    } from "./context/context";
import {
      defaultUserDetails
    , defaultLocationSettings
    , defaultLayer
    , defaultModeSettings
    , defaultMapSettings
    } from "./context/defaults";
import { Box, IconButton, Tooltip } from '@mui/material';
import { AppShell } from '@liftango/ui-app-shell';
import { Icon } from '@liftango/ui-icons';

import Map from "./components/map/map";
import LayersPanel from './components/layer_panel/layersPanel';
import AuthModal from "./components/authentication/modals/AuthenticationModal";
import SimulationPage from "./pages/SimulationPage";
import SimulationResultsPage from "./pages/SimulationResultsPage";
import SimulationReportPage from "./pages/SimulationReportPage";
import ProfilePage from "./pages/ProfilePage";
import CreditsPage from "./pages/CreditsPage";
import AutopilotDashboardPage from "./pages/AutopilotDashboardPage";
import FleetOptimisationPage from "./pages/FleetOptimisationPage";
import ToolsPage from "./pages/ToolsPage";
import PostcodeStopAssignmentPage from './pages/PostcodeStopAssignmentPage';
import Navigation from './components/Navigation';
import LicenseBanner from './components/licensing/LicenseBanner';
import RouteAnalyticsTracker from './analytics/RouteAnalyticsTracker';
import { LAYER_PANEL_COLLAPSED_WIDTH } from './components/layer_panel/layerPanelStyles';
import { useLeftSidebarResize, LEFT_SIDEBAR_RESIZE_HANDLE_WIDTH } from './hooks/useLeftSidebarResize';
import { opsColors } from './theme/opsTokens';

import { getToken, resolveRoleFromDecoded } from './components/authentication/functions/authentication_management';
import {jwtDecode} from "jwt-decode";
import {DecodedAuthToken} from "./components/authentication/types/AuthTypes";
import { initAnalytics, logEvent } from "./services/analytics";
import { diagnosticsCollector } from './services/diagnostics/diagnosticsCollector';
import { setupBackendAnalyticsLifecycle } from './services/backendAnalytics';
import { subscribeAtlasUnauthorized } from './services/api/atlasBackendClient';
import { useTranslation } from 'react-i18next';
import {
    applyAutomationModeDocumentFlag,
    emitAutomationEvent,
    isAutomationModeEnabled,
    updateAgentContract,
} from './automation/agentAutomation';

// Import simulation test functions for console testing (development only)
import './services/simulation/testSimulation';

// Import SimulationProvider for global simulation state
import { SimulationProvider } from './context/SimulationContext';
import { SimulationResultsProvider } from './context/SimulationResultsContext';
import { SpatialAnalysisProvider } from './context/SpatialAnalysisContext';

// Import FixedRoutesProvider for fixed routes state
import { FixedRoutesProvider } from './context/FixedRoutesContext';
import { NetworkBrowseProvider } from './context/NetworkBrowseContext';
import { RouteOverlapProvider } from './context/RouteOverlapContext';

// Import RidesContextProvider for rides state
import { RidesContextProvider } from './context/RidesContext';

// Import geolocation hook
import { useGeolocation } from './hooks/useGeolocation';
import { getUserMapViewport, storeUserMapViewport } from './utils/locationStorage';

// Import FeatureAvailabilityProvider
import { FeatureAvailabilityProvider } from './services/featureAvailability/featureAvailabilityContext';

// Import FeatureFlagProvider
import { FeatureFlagProvider } from './services/featureFlags/featureFlagContext';

// Import admin components
import AdminPage from './pages/AdminPage';
import AdminRouteGuard from './components/admin/AdminRouteGuard';

// Import company components
import RegisterPage from './pages/RegisterPage';
import CompanyPage from './pages/CompanyPage';
import CompanyRouteGuard from './components/company/CompanyRouteGuard';
import RulesEnginePage from './pages/RulesEnginePage';

// Import password reset pages
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

type GeoLayers = Layer[]

function App() {
    const { t, i18n } = useTranslation('map');
    const location = useLocation();
    const automationModeEnabled = isAutomationModeEnabled();
    const [mapName, setMapName] = useState(() => t('layers.defaultMapName'))
    const [mapCentre, setMapCentre] = useState<LocationSettingsContext>(defaultLocationSettings)
    const [basemap, setBasemap] = useState(defaultMapSettings.basemap)
    const [userDetails, setUserDetails] = useState<UserDetails>(defaultUserDetails)
    const [layers, setLayers] = useState<GeoLayers>(() => [{ ...defaultLayer, layerName: t('layers.defaultLayerName') }])
    const [activeLayerId, setActiveLayerId] = useState(0)
    const [idCounter, setIdCounter] = useState(0)

    // Deduplicate layers whenever layers change - ensure no duplicate layerIds
    useEffect(() => {
        // Check for duplicate layerIds
        const layerIds = new Set<number>();
        const duplicates: number[] = [];
        
        layers.forEach((layer) => {
            if (layerIds.has(layer.layerId)) {
                duplicates.push(layer.layerId);
            } else {
                layerIds.add(layer.layerId);
            }
        });
        
        // If duplicates found, remove them (keep first occurrence)
        if (duplicates.length > 0) {
            // console.warn(`Found ${duplicates.length} duplicate layer(s), removing duplicates:`, duplicates);
            const seenIds = new Set<number>();
            const deduplicatedLayers = layers.filter((layer) => {
                if (seenIds.has(layer.layerId)) {
                    return false; // Skip duplicate
                }
                seenIds.add(layer.layerId);
                return true; // Keep first occurrence
            });
            setLayers(deduplicatedLayers);
            
            // Track deduplication
            logEvent({
                category: 'Layer Management',
                action: 'LayersDeduplicated',
                label: 'Duplicate Layers Removed',
                duplicate_count: duplicates.length,
                total_layers_before: layers.length,
                total_layers_after: deduplicatedLayers.length
            });
        }
    }, [layers])
    const [selectedShapes, setSelectedShapes] = useState<string[]>([])
    const [fleets, setFleets] = useState<Fleet[]>([{
        id: 'fleet-1',
        name: 'Fleet 1',
        vehicles: [],
    }])
    const [services, setServices] = useState<Service[]>([])
    const [modeSettings, setModeSettings] = useState<ModeSettings>(defaultModeSettings)
    const [openSimulationModal, setOpenSimulationModal] = useState<MapActionsContext['openSimulationModal']>(null)
    const [isLayersPanelCollapsed, setIsLayersPanelCollapsed] = useState(false)
    const {
      widthPx: layersPanelWidth,
      isResizing: isSidebarResizing,
      handleResizeMouseDown: handleSidebarResizeMouseDown,
      handleResizeTouchStart: handleSidebarResizeTouchStart,
      nudgeWidth: nudgeSidebarWidth,
    } = useLeftSidebarResize();

    const registerOpenSimulationModal = useCallback((handler: MapActionsContext['openSimulationModal']) => {
        setOpenSimulationModal(() => handler);
    }, []);

    // Migrate untouched legacy defaults when locale changes.
    useEffect(() => {
        if (mapName === 'My Map') {
            setMapName(t('layers.defaultMapName'));
        }

        setLayers((prev) => {
            let changed = false;
            const next = prev.map((layer) => {
                if (layer.layerName === 'New Layer') {
                    changed = true;
                    return { ...layer, layerName: t('layers.defaultLayerName') };
                }
                return layer;
            });
            return changed ? next : prev;
        });
    }, [i18n.language, mapName, t]);

    // Use geolocation hook
    const { location: userLocation, isLoading: locationLoading, error: locationError, source } = useGeolocation();
    const [hasHydratedViewport, setHasHydratedViewport] = useState(false);
    const geolocationAppliedRef = useRef(false);

    // Hydrate map viewport from per-user cache on entry/login before geolocation can override it.
    useEffect(() => {
        const cachedViewport = getUserMapViewport(userDetails.userId);
        if (cachedViewport?.location) {
            setMapCentre(cachedViewport.location);
            setHasHydratedViewport(true);
            geolocationAppliedRef.current = true;
            return;
        }

        setHasHydratedViewport(false);
        geolocationAppliedRef.current = false;
    }, [userDetails.userId]);

    // Update map center when user location is available
    useEffect(() => {
        if (hasHydratedViewport || geolocationAppliedRef.current) {
            return;
        }
        if (userLocation && !locationLoading) {
            // console.log(`Setting map center from ${source}:`, userLocation);
            setMapCentre({
                ...userLocation,
                zoom: Math.max(userLocation.zoom, defaultLocationSettings.zoom)
            });
            geolocationAppliedRef.current = true;
        }
    }, [userLocation, locationLoading, source, hasHydratedViewport]);

    // Persist current viewport per user for fast restore on next entry.
    useEffect(() => {
        storeUserMapViewport(userDetails.userId, mapCentre);
    }, [userDetails.userId, mapCentre]);

    // Log location errors for debugging
    useEffect(() => {
        if (locationError) {
            // console.warn('Geolocation error:', locationError);
        }
    }, [locationError]);

    const globals: GlobalContext = {
        mapLayers: layers,
        setLayers: setLayers,
        activeLayerId: activeLayerId,
        setActiveLayerId: setActiveLayerId,
        idCounter: idCounter,
        setIdCounter: setIdCounter,
        selectedShapes: selectedShapes,
        setSelectedShapes: setSelectedShapes,
        fleets: fleets,
        setFleets: setFleets,
        services: services,
        setServices: setServices
    }

    const mapSettings: MapSettingsContext = {
        mapName: mapName,
        setMapName: setMapName,
        mapCentre: mapCentre,
        setMapCentre: setMapCentre,
        basemap: basemap,
        setBasemap: setBasemap
    }

    const userData: UserData = {
        userDetails: userDetails,
        setUserDetails: setUserDetails
    }

    const appContext: Context = {
        global: globals,
        mapSettings: mapSettings,
        mapActions: {
            openSimulationModal: openSimulationModal,
        },
        userData: userData,
        modeSettings: modeSettings,
        setModeSettings: setModeSettings
    }

    useEffect(() => {
        diagnosticsCollector.installGlobalErrorListeners();
        setupBackendAnalyticsLifecycle();
        applyAutomationModeDocumentFlag(automationModeEnabled);
        updateAgentContract({
            routeId: location.pathname,
            readiness: { appReady: true },
        });
        emitAutomationEvent('app_initialized', { routeId: location.pathname });
        return () => {
            diagnosticsCollector.uninstallGlobalErrorListeners();
        };
    }, [automationModeEnabled, location.pathname]);

    useEffect(() => {
        diagnosticsCollector.addBreadcrumb('navigation', 'route_changed', {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash,
        });
        updateAgentContract({
            routeId: location.pathname,
            readiness: { routeReady: true },
        });
        emitAutomationEvent('route_changed', {
            routeId: location.pathname,
            search: location.search,
        });
    }, [location.pathname, location.search, location.hash]);

    useEffect(() => {
        const token = getToken();

        if (token) {
            const decodedToken = jwtDecode<DecodedAuthToken>(token.access_token)
            initAnalytics(decodedToken.user_id);
            setUserDetails({
                email: decodedToken.sub,
                userId: decodedToken.user_id,
                fullName: '',
                role: resolveRoleFromDecoded(decodedToken),
                customerId: decodedToken.customer_id ?? null,
                customerName: decodedToken.customer_name ?? null,
                companyRole: decodedToken.company_role ?? null,
            })
        } else {
            initAnalytics();
        }
    }, []);

    useEffect(() => {
        return subscribeAtlasUnauthorized(() => {
            setUserDetails(defaultUserDetails);
        });
    }, []);

    useEffect(() => {
        const authState = userDetails.userId ? 'authenticated' : 'anonymous';
        updateAgentContract({ authState });
    }, [userDetails.userId]);

    // Standalone pages (no nav, no auth modal)
    const isStandaloneRoute =
        location.pathname === '/register' ||
        location.pathname === '/forgot-password' ||
        location.pathname.startsWith('/reset-password/');

    return (
        <DndProvider backend={HTML5Backend}>
            <FeatureAvailabilityProvider userId={userDetails.userId}>
            <FeatureFlagProvider userId={userDetails.userId}>
                <SimulationResultsProvider>
                <SpatialAnalysisProvider>
                <SimulationProvider>
                    <FixedRoutesProvider>
                        <NetworkBrowseProvider>
                        <RouteOverlapProvider>
                        <RidesContextProvider>
                            <AppContext.Provider value={appContext}>
                            {isStandaloneRoute ? (
                                <Routes>
                                    <Route path="/register" element={<RegisterPage />} />
                                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                                    <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                                </Routes>
                            ) : (
                            <div
                                className="App"
                                data-testid="app.shell.root"
                                data-app-ready="true"
                                data-route-ready="true"
                                data-route-id={location.pathname}
                                data-auth-state={userDetails.userId ? 'authenticated' : 'anonymous'}
                                data-automation-mode={automationModeEnabled ? 'true' : 'false'}
                            >
                        <AppShell style={{ height: '100vh' }}>
                            <AppShell.TopBar height={72} style={{ position: 'relative', zIndex: 2300 }}>
                                <Navigation />
                            </AppShell.TopBar>
                            {location.pathname === '/' && (
                              <AppShell.SideNav
                                width={isLayersPanelCollapsed ? LAYER_PANEL_COLLAPSED_WIDTH : layersPanelWidth}
                                // Disable the width transition while dragging so the panel tracks the
                                // cursor 1:1; keep it for the collapse/expand animation otherwise.
                                style={isSidebarResizing ? { transition: 'none' } : undefined}
                              >
                                <Box sx={{
                                  position: 'relative',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  height: '100%',
                                  p: isLayersPanelCollapsed ? 0.25 : 0.5,
                                  overflow: 'visible',
                                  // Reveal the collapse toggle only when the sidebar is hovered or
                                  // keyboard-focused (matches the ops-portal edge control).
                                  '&:hover .sidebar-collapse-toggle, &:focus-within .sidebar-collapse-toggle': {
                                    opacity: 1,
                                  },
                                }}>
                                  <LayersPanel
                                    isCollapsed={isLayersPanelCollapsed}
                                  />
                                  {!isLayersPanelCollapsed && (
                                    <Box
                                      role="separator"
                                      aria-orientation="vertical"
                                      aria-label="Resize sidebar"
                                      tabIndex={0}
                                      onMouseDown={handleSidebarResizeMouseDown}
                                      onTouchStart={handleSidebarResizeTouchStart}
                                      onKeyDown={(e) => {
                                        const step = e.shiftKey ? 48 : 16;
                                        if (e.key === 'ArrowRight') { e.preventDefault(); nudgeSidebarWidth(step); }
                                        else if (e.key === 'ArrowLeft') { e.preventDefault(); nudgeSidebarWidth(-step); }
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        bottom: 0,
                                        right: 0,
                                        width: LEFT_SIDEBAR_RESIZE_HANDLE_WIDTH,
                                        cursor: 'ew-resize',
                                        touchAction: 'none',
                                        zIndex: 3,
                                        backgroundColor: isSidebarResizing ? opsColors.brand.primary : 'transparent',
                                        opacity: isSidebarResizing ? 0.5 : 1,
                                        transition: 'background-color 0.15s ease, opacity 0.15s ease',
                                        '&:hover, &:focus-visible': {
                                          backgroundColor: opsColors.brand.primary,
                                          opacity: 0.35,
                                        },
                                      }}
                                    />
                                  )}
                                  <Tooltip title={isLayersPanelCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}>
                                    <IconButton
                                      className="sidebar-collapse-toggle"
                                      aria-label={isLayersPanelCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                                      onClick={() => setIsLayersPanelCollapsed(prev => !prev)}
                                      sx={{
                                        position: 'absolute',
                                        right: 6,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        width: 28,
                                        height: 28,
                                        borderRadius: '999px',
                                        backgroundColor: opsColors.neutral.panel,
                                        border: `1px solid ${opsColors.brand.primary}`,
                                        color: opsColors.brand.primary,
                                        // Sit just above the panel content but inside the sidebar's
                                        // stacking context, so it never floats over modals/drawers.
                                        zIndex: 2,
                                        opacity: 0,
                                        transition: 'opacity 0.2s ease, background-color 120ms ease',
                                        '&:hover': {
                                          backgroundColor: opsColors.neutral.panelAlt,
                                        },
                                        '&:focus-visible': {
                                          opacity: 1,
                                        },
                                      }}
                                    >
                                      <Icon size={15} name={isLayersPanelCollapsed ? 'chevron-right' : 'chevron-left'} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </AppShell.SideNav>
                            )}
                            <AppShell.Content>
                                <AuthModal />
                                <LicenseBanner />
                                <RouteAnalyticsTracker />
                                <Routes>
                                    <Route path="/" element={<Map registerSimulationModalOpen={registerOpenSimulationModal} />} />
                                    <Route path="/simulation" element={<SimulationPage />} />
                                    <Route path="/simulation-results" element={<SimulationResultsPage />} />
                                    <Route path="/simulation-results/report" element={<SimulationReportPage />} />
                                    <Route path="/tools" element={<ToolsPage />} />
                                    <Route
                                        path="/fleet-optimisation"
                                        element={<FleetOptimisationPage />}
                                    />
                                    <Route
                                        path="/tools/postcode-stop-assignment"
                                        element={<PostcodeStopAssignmentPage />}
                                    />
                                    <Route path="/profile" element={<ProfilePage />} />
                                    <Route path="/credits" element={<CreditsPage />} />
                                    <Route path="/builder" element={<AutopilotDashboardPage />} />
                                    <Route
                                        path="/admin"
                                        element={
                                            <AdminRouteGuard>
                                                <AdminPage />
                                            </AdminRouteGuard>
                                        }
                                    />
                                    <Route
                                        path="/company"
                                        element={
                                            <CompanyRouteGuard>
                                                <CompanyPage />
                                            </CompanyRouteGuard>
                                        }
                                    />
                                    <Route path="/rules-engine" element={<RulesEnginePage />} />
                                </Routes>
                            </AppShell.Content>
                        </AppShell>
                        </div>
                            )}
                            </AppContext.Provider>
                        </RidesContextProvider>
                        </RouteOverlapProvider>
                        </NetworkBrowseProvider>
                    </FixedRoutesProvider>
                </SimulationProvider>
                </SpatialAnalysisProvider>
                </SimulationResultsProvider>
            </FeatureFlagProvider>
            </FeatureAvailabilityProvider>
        </DndProvider>
    );
}

export default App;
